import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import MuiCard from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Divider, Alert, IconButton } from "@mui/material";
import InputAdornment from "@mui/material/InputAdornment";

import { Link as RouterLink, useNavigate } from "react-router-dom";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import SospitaLogo from "./CustomIcons";
import { useTranslation } from "react-i18next";
import { useAuthService } from "@services/useAuthService";
import { useSession } from "@hooks/useSession";
import ForgotPassword from "./ForgotPassword";
import CircularProgress from "@mui/material/CircularProgress";
import Toast from "./toast";
import useApiErrorHandler from "@/hooks/useApiErrorHandler";
import Select from "react-select";
import { getSingleSelectStyles } from "@/lib/styles";
import { useTheme } from "@mui/material/styles";
import MuiLink from "@mui/material/Link";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import LockIcon from "@mui/icons-material/Lock";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import SecurityIcon from "@mui/icons-material/Security";
import { useThemeContext } from "@/config/useThemeContext";
import useGetSupportedLanguages from "@/hooks/useGetSupportedLanguages";

const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(3),
  gap: theme.spacing(2),
  boxShadow: theme.shadows[8],
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  background: theme.palette.background.paper,
  maxHeight: "calc(100vh - 48px)",
  overflowY: "auto",
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(3.5),
  },
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: theme.palette.divider,
    borderRadius: "4px",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: theme.spacing(1),
    transition: theme.transitions.create(["border-color", "box-shadow"]),
    "&:hover": {
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.palette.primary.main,
      },
    },
    "&.Mui-focused": {
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}20`,
    },
  },
}));

export default function SignInCard() {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const { palette } = useThemeContext(); // Add this line to get current palette
  const singleSelectStyles = getSingleSelectStyles(theme);
  const { setLoading } = useSession();
  const { toast, setToast, handleToastClose } = useApiErrorHandler();
  const navigate = useNavigate();
  const { signin } = useAuthService();
  const [forgotPassword, setForgotPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [portalEl, setPortalEl] = useState(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, touchedFields },
    reset,
  } = useForm({
    mode: "onBlur", // Only validate when field loses focus
    reValidateMode: "onChange",
    defaultValues: {
      orgId: "",
      username: "",
      password: "",
    },
  });

  useEffect(() => {
    setPortalEl(document.body);

    // Ensure i18n language matches localStorage on mount
    const savedLang = localStorage.getItem("ln");
    if (savedLang && savedLang !== i18n.language) {
      i18n.changeLanguage(savedLang);
    }
  }, [i18n]);

  const { languageOptions, isLoading: languagesLoading } =
    useGetSupportedLanguages();

  // Get current language from localStorage or i18n current language
  const getCurrentLanguageOption = () => {
    const currentLang = localStorage.getItem("ln") || i18n.language || "en";
    if (languageOptions.length > 0) {
      return (
        languageOptions.find((o) => o.value === currentLang) ||
        languageOptions.find((o) => o.value === "en") ||
        languageOptions[0]
      );
    }
    return { value: "en", label: "🇺🇸 English", name: "English" };
  };

  const [currentLanguageOption, setCurrentLanguageOption] = useState(() =>
    getCurrentLanguageOption()
  );

  // Update current language option when language options are loaded
  useEffect(() => {
    if (languageOptions.length > 0) {
      const currentLang = localStorage.getItem("ln") || i18n.language || "en";
      const newCurrentOption =
        languageOptions.find((o) => o.value === currentLang) ||
        languageOptions.find((o) => o.value === "en") ||
        languageOptions[0];
      setCurrentLanguageOption(newCurrentOption);
      // Ensure i18n language matches localStorage
      if (newCurrentOption.value !== i18n.language) {
        i18n.changeLanguage(newCurrentOption.value);
      }
    }
  }, [languageOptions, i18n]);

  const handleLanguageChange = (opt) => {
    localStorage.setItem("ln", opt.value);
    i18n.changeLanguage(opt.value);
    setCurrentLanguageOption(opt);
  };

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("credentials") || "null");
    if (stored) {
      reset({
        orgId: stored.orgId,
        username: stored.username,
        password: stored.password,
      });
      setRememberMe(stored.rememberMe === true);
    }
  }, [reset]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    setLoading(true);
    try {
      await signin(
        {
          organization_acronym: data.orgId.trim(),
          username: data.username.trim(),
          password: data.password,
        },
        i18n.language
      );
      if (rememberMe) {
        localStorage.setItem(
          "credentials",
          JSON.stringify({
            orgId: data.orgId.trim(),
            username: data.username.trim(),
            password: data.password,
            language: i18n.language,
            rememberMe: true,
            themePalette: palette, // Store the current theme palette
          })
        );
      } else {
        localStorage.removeItem("credentials");
      }

      setToast({
        open: true,
        severity: "success",
        message: t("signInCard.successMessage"),
      });
      navigate("/");
    } catch (err) {
      // Show the API error message if available
      const errorMessage =
        err.response?.data?.message ||
        t(
          "signInCard.invalidCredentials",
          "Invalid credentials. Please check your Organization ID, Username, and Password and try again."
        );

      setToast({
        open: true,
        severity: "error",
        message: errorMessage,
        autoHideDuration: 5000,
      });

      // Clear only the password field
      reset({
        ...data,
        password: "",
      });
    } finally {
      setIsLoading(false);
      setLoading(false);
    }
  };

  const getFieldValidation = (fieldName) => {
    const hasError = errors[fieldName];
    const isTouched = touchedFields[fieldName];

    // Only show error state if field has been touched and has error
    if (isTouched && hasError) return "error";
    // Show default state if not touched or while typing
    return "default";
  };

  return (
    <>
      <Card variant="outlined">
        {/* Header Section */}
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              justifyContent: "center",
              mb: 2,
            }}
          >
            <SospitaLogo fontSize="large" />
          </Box>
          <Typography
            component="h1"
            variant="h3"
            sx={{
              fontWeight: 600,
              fontSize: "clamp(1.75rem, 4vw, 2.25rem)",
              color: "text.primary",
              mb: 1,
            }}
          >
            {t("signInCard.title")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {t("signInCard.subtitle", "Access your enterprise safety platform")}
          </Typography>

          {/* Language Selection */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <Box sx={{ minWidth: 200 }}>
              <Select
                value={currentLanguageOption}
                onChange={handleLanguageChange}
                options={
                  languageOptions.length > 0
                    ? languageOptions
                    : [currentLanguageOption]
                }
                isLoading={languagesLoading}
                styles={{
                  ...singleSelectStyles,
                  control: (base, state) => ({
                    ...base,
                    minHeight: "36px",
                    fontSize: "14px",
                    borderColor: state.isFocused
                      ? theme.palette.primary.main
                      : theme.palette.divider,
                    boxShadow: state.isFocused
                      ? `0 0 0 2px ${theme.palette.primary.main}20`
                      : "none",
                    "&:hover": {
                      borderColor: theme.palette.primary.main,
                    },
                  }),
                  menuPortal: (base) => ({ ...base, zIndex: 1301 }),
                }}
                menuPortalTarget={portalEl}
                menuPosition="fixed"
                isSearchable={false}
                placeholder={t("signInCard.languagePlaceholder")}
              />
            </Box>
          </Box>
        </Box>

        {/* Security Notice */}
        <Alert
          severity="info"
          icon={<SecurityIcon />}
          sx={{ mb: 3, borderRadius: 2 }}
        >
          <Typography variant="body2">
            {t(
              "signInCard.securityNotice",
              "Your session is encrypted and secure"
            )}
          </Typography>
        </Alert>

        {/* Sign In Form */}
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <StyledTextField
            label={t("signInCard.orgIdLabel")}
            autoComplete="organization"
            type="text"
            {...register("orgId", {
              required: t("signInCard.orgIdRequired"),
              pattern: {
                value: /^[A-Za-z0-9_-]+$/,
                message: t(
                  "signInCard.orgIdPattern",
                  "Only letters, numbers, hyphens and underscores allowed"
                ),
              },
              reValidateMode: "onChange",
              minLength: {
                value: 2,
                message: t(
                  "signInCard.orgIdMinLength",
                  "Organization ID must be at least 2 characters"
                ),
              },
            })}
            error={!!errors.orgId}
            helperText={errors?.orgId?.message}
            slotProps={{
              input: {
                "aria-label": t(
                  "signInCard.orgIdAria",
                  "Organization ID input field"
                ),
                startAdornment: (
                  <InputAdornment position="start">
                    <BusinessIcon
                      color={
                        getFieldValidation("orgId") === "error"
                          ? "error"
                          : "action"
                      }
                    />
                  </InputAdornment>
                ),
              },
            }}
            fullWidth
            variant="outlined"
          />

          <StyledTextField
            label={t("signInCard.usernameLabel")}
            autoComplete="username"
            type="text"
            {...register("username", {
              required: t("signInCard.usernameRequired"),
              minLength: {
                value: 3,
                message: t("signInCard.usernameMinLength"),
              },
              pattern: {
                value: /^[A-Za-z0-9._@-]+$/,
                message: t(
                  "signInCard.usernamePattern",
                  "Invalid username format"
                ),
              },
            })}
            error={!!errors.username}
            helperText={errors?.username?.message}
            slotProps={{
              input: {
                "aria-label": t(
                  "signInCard.usernameAria",
                  "Username input field"
                ),
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonIcon
                      color={
                        getFieldValidation("username") === "error"
                          ? "error"
                          : "action"
                      }
                    />
                  </InputAdornment>
                ),

                autoComplete: "username",
              },
            }}
            fullWidth
            variant="outlined"
          />

          <StyledTextField
            label={t("signInCard.passwordLabel")}
            type={showPassword ? "text" : "password"}
            autoComplete="current-password"
            {...register("password", {
              required: t("signInCard.passwordRequired"),
              minLength: {
                value: 6,
                message: t("signInCard.passwordMinLength"),
              },
            })}
            error={!!errors.password}
            helperText={errors?.password?.message}
            slotProps={{
              input: {
                "aria-label": t(
                  "signInCard.passwordAria",
                  "Password input field"
                ),
                autoComplete: "current-password",
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon
                      color={
                        getFieldValidation("password") === "error"
                          ? "error"
                          : "action"
                      }
                    />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={() => setShowPassword((s) => !s)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            fullWidth
            variant="outlined"
          />

          {/* Form Controls */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  {t("signInCard.rememberMeLabel")}
                </Typography>
              }
            />
            <MuiLink
              component="button"
              type="button"
              onClick={() => setForgotPassword(true)}
              variant="body2"
              sx={{ fontWeight: 500 }}
            >
              {t("signInCard.forgotPasswordLink")}
            </MuiLink>
          </Box>

          {/* Submit Button */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={isLoading || !isValid}
            sx={{
              py: 1.5,
              fontSize: "1rem",
              fontWeight: 600,
              borderRadius: 2,
              textTransform: "none",
              boxShadow: theme.shadows[2],
              "&:hover": {
                boxShadow: theme.shadows[4],
              },
            }}
          >
            {isLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                <span>{t("signInCard.signingIn", "Signing in...")}</span>
              </Box>
            ) : (
              t("signInCard.submitButton")
            )}
          </Button>

          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {t("signInCard.orDivider")}
            </Typography>
          </Divider>

          {/* Sign Up Link */}
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {t("signInCard.noAccountText")}{" "}
              <MuiLink
                component={RouterLink}
                to="/sign-up"
                variant="body2"
                sx={{ fontWeight: 500, textDecoration: "none" }}
              >
                {t("signInCard.signUpLink")}
              </MuiLink>
            </Typography>
          </Box>
        </Box>

        <Toast
          open={toast.open}
          severity={toast.severity}
          message={toast.message}
          onClose={handleToastClose}
        />
      </Card>

      {forgotPassword && (
        <ForgotPassword
          open={forgotPassword}
          onClose={() => setForgotPassword(false)}
          onSuccess={(message, severity = "success") => {
            setToast({
              open: true,
              severity: severity,
              message: message,
              autoHideDuration: 6000,
            });
          }}
        />
      )}
    </>
  );
}
